import { Button, IconButton, Input, Select } from '@artosera/ui';
import { TYPES_MEDIA, type TypeMedia } from '@artosera/shared';
import { ImagePlus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { estApiError } from '../../../api/errors.js';
import { useTranslation } from 'react-i18next';
import {
  useApercuMedia,
  useDeposerMedia,
  useModifierMedia,
  useSupprimerMedia,
} from '../../../api/medias.js';
import type { VueAccrochage } from '../../../api/expositions.js';
import styles from './Tabs.module.css';

export interface MediasTabProps {
  galerieSlug: string;
  expositionId: string;
  vuesAccrochage: readonly VueAccrochage[];
  peutEcrire: boolean;
}

/**
 * Médias (S3-d) : les vues d'accrochage d'une exposition, rattachement
 * `exhibition` ouvert depuis § 15.11. Composants réutilisés de
 * `oeuvres/tabs/MediasTab.tsx` ; pas de route d'ordre groupé pour les
 * expositions (contrairement aux œuvres), donc pas de glisser-déposer ici.
 */
export function MediasTab({
  galerieSlug,
  expositionId,
  vuesAccrochage,
  peutEcrire,
}: MediasTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const ordonnees = [...vuesAccrochage].sort((a, b) => a.ordre - b.ordre);

  const deposer = useDeposerMedia(galerieSlug);
  const fichierRef = useRef<HTMLInputElement>(null);
  const [survoleDepot, setSurvoleDepot] = useState(false);

  function deposerFichier(fichier: File): void {
    deposer.mutate({ fichier, rattachementType: 'exhibition', rattachementId: expositionId });
  }

  // Un fichier refusé dit pourquoi (type, poids), comme dans le coffre.
  const erreurDepot = deposer.isError
    ? estApiError(deposer.error)
      ? deposer.error.message
      : t('commun:erreurReseau')
    : null;

  return (
    <div className={styles.formulaire}>
      {peutEcrire ? (
        <div
          className={styles.zoneDepot}
          data-survole={survoleDepot ? 'true' : 'false'}
          onDragOver={(event) => {
            event.preventDefault();
            setSurvoleDepot(true);
          }}
          onDragLeave={() => setSurvoleDepot(false)}
          onDrop={(event) => {
            event.preventDefault();
            setSurvoleDepot(false);
            const fichier = event.dataTransfer.files[0];
            if (fichier) deposerFichier(fichier);
          }}
        >
          <ImagePlus size={24} strokeWidth={1.5} aria-hidden />
          <p>{t('regie:expositions.medias.glisserDeposer')}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fichierRef.current?.click()}
            loading={deposer.isPending}
          >
            {t('regie:expositions.medias.deposer')}
          </Button>
          <input
            ref={fichierRef}
            type="file"
            accept="image/*"
            aria-label={t('regie:expositions.medias.deposer')}
            hidden
            onChange={(event) => {
              const fichier = event.target.files?.[0];
              if (fichier) deposerFichier(fichier);
              event.target.value = '';
            }}
          />
        </div>
      ) : null}

      {erreurDepot ? (
        <p className={styles.erreur} role="alert">
          {erreurDepot}
        </p>
      ) : null}

      {ordonnees.length === 0 ? (
        <p className={styles.aide}>{t('regie:expositions.medias.aucunMedia')}</p>
      ) : (
        <div className={styles.grilleMedias}>
          {ordonnees.map((media) => (
            <MediaCarte
              key={media.id}
              galerieSlug={galerieSlug}
              media={media}
              peutEcrire={peutEcrire}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaCarte({
  galerieSlug,
  media,
  peutEcrire,
}: {
  galerieSlug: string;
  media: VueAccrochage;
  peutEcrire: boolean;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const apercu = useApercuMedia(galerieSlug, media.id);
  const modifier = useModifierMedia(galerieSlug);
  const supprimer = useSupprimerMedia(galerieSlug);

  const [type, setType] = useState<TypeMedia>(media.type as TypeMedia);
  const [credit, setCredit] = useState(media.credit ?? '');
  const [legendeFr, setLegendeFr] = useState(media.legende?.fr ?? '');
  const [legendeEn, setLegendeEn] = useState(media.legende?.en ?? '');

  return (
    <div className={styles.carteMedia}>
      {apercu.data ? (
        <img className={styles.vignetteMedia} src={apercu.data} alt="" />
      ) : (
        <div className={styles.vignetteMedia} aria-hidden />
      )}

      <div className={styles.champsMedia}>
        <Select
          aria-label={t('regie:expositions.medias.typeVue')}
          value={type}
          onValueChange={(valeur) => setType(valeur as TypeMedia)}
          options={TYPES_MEDIA.map((code) => ({ value: code, label: t(`oeuvres:medias.${code}`) }))}
          disabled={!peutEcrire}
        />
        <Input
          aria-label={t('regie:expositions.medias.credit')}
          placeholder={t('regie:expositions.medias.credit')}
          value={credit}
          disabled={!peutEcrire}
          onChange={(event) => setCredit(event.target.value)}
        />
        <Input
          aria-label={t('regie:expositions.medias.legendeFr')}
          placeholder={t('regie:expositions.medias.legendeFr')}
          value={legendeFr}
          disabled={!peutEcrire}
          onChange={(event) => setLegendeFr(event.target.value)}
        />
        <Input
          aria-label={t('regie:expositions.medias.legendeEn')}
          placeholder={t('regie:expositions.medias.legendeEn')}
          value={legendeEn}
          disabled={!peutEcrire}
          onChange={(event) => setLegendeEn(event.target.value)}
        />
      </div>

      {peutEcrire ? (
        <div className={styles.champsMedia}>
          <Button
            variant="secondary"
            size="sm"
            loading={modifier.isPending}
            onClick={() =>
              modifier.mutate({
                id: media.id,
                corps: { type, credit: credit || null, legende: { fr: legendeFr, en: legendeEn } },
              })
            }
          >
            {t('commun:actions.enregistrer')}
          </Button>
          <IconButton
            variant="outline"
            icon={<Trash2 aria-hidden />}
            aria-label={t('regie:expositions.medias.supprimer')}
            size="sm"
            onClick={() => {
              if (window.confirm(t('regie:expositions.medias.supprimer')))
                supprimer.mutate(media.id);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
